import React from 'react';
import { ArrowUpDown, Filter, Search, X } from '../../../bravosIcons';
import { LibraryFilterPopover, type LibFilterOption } from '../components/LibraryFilterPopover';
import type { LibraryExercisesApi } from '../../hooks/useLibraryExercises';
import { LIB_EX_SORT_OPTIONS } from '../../types/library';

type ExercisesToolbarDesktopProps = {
  ex: LibraryExercisesApi;
  activeTab?: string;
};

/** Desktop Library — Exercises tab toolbar (§4). */
export function ExercisesToolbarDesktop({ ex, activeTab }: ExercisesToolbarDesktopProps) {
  if (activeTab != null && activeTab !== 'exercises') return null;

  return (
    <div className="lib-toolbar">
      <div className="lib-search">
        <Search />
        <input
          type="text"
          value={ex.exerciseNameFilter}
          onChange={(e) => ex.setExerciseNameFilter(e.target.value)}
          placeholder="Search exercise name…"
          aria-label="Search exercises by name"
        />
      </div>

      <div className="relative" ref={ex.libExerciseMuscleChipRef}>
        <button
          type="button"
          className={`lib-chip ${(ex.libExerciseOpenChip === 'muscle' || ex.libExerciseMuscleFilter.size > 0) ? 'is-active' : ''}`}
          onClick={() => ex.setLibExerciseOpenChip(ex.libExerciseOpenChip === 'muscle' ? null : 'muscle')}
          aria-haspopup="dialog"
          aria-expanded={ex.libExerciseOpenChip === 'muscle'}
        >
          <Filter />
          Muscle group
          {ex.libExerciseMuscleFilter.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{ex.libExerciseMuscleFilter.size}</span>
          )}
        </button>
        {ex.libExerciseOpenChip === 'muscle' && (
          <LibraryFilterPopover
            options={[
              ...ex.libExerciseMuscleOptions.values.map<LibFilterOption>((opt) => ({
                id: opt,
                label: opt,
                matchCount: ex.libExerciseMuscleMatchCounts.counts.get(opt) ?? 0,
              })),
              ...(ex.libExerciseMuscleOptions.hasUnspecified
                ? [{
                    id: ex.LIB_EX_UNSPECIFIED,
                    label: 'Unspecified',
                    matchCount: ex.libExerciseMuscleMatchCounts.unspecified,
                  } as LibFilterOption]
                : []),
            ]}
            selected={ex.libExerciseMuscleFilter}
            onChange={(next) => ex.setLibExerciseMuscleFilter(next as Set<string>)}
            onClose={() => ex.setLibExerciseOpenChip(null)}
            emptyMessage="No muscle groups in your library yet."
            ariaLabel="Muscle group filter"
          />
        )}
      </div>

      <div className="relative" ref={ex.libExerciseEquipmentChipRef}>
        <button
          type="button"
          className={`lib-chip ${(ex.libExerciseOpenChip === 'equipment' || ex.libExerciseEquipmentFilter.size > 0) ? 'is-active' : ''}`}
          onClick={() => ex.setLibExerciseOpenChip(ex.libExerciseOpenChip === 'equipment' ? null : 'equipment')}
          aria-haspopup="dialog"
          aria-expanded={ex.libExerciseOpenChip === 'equipment'}
        >
          <Filter />
          Equipment
          {ex.libExerciseEquipmentFilter.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{ex.libExerciseEquipmentFilter.size}</span>
          )}
        </button>
        {ex.libExerciseOpenChip === 'equipment' && (
          <LibraryFilterPopover
            options={[
              ...ex.libExerciseEquipmentOptions.values.map<LibFilterOption>((opt) => ({
                id: opt,
                label: opt,
                matchCount: ex.libExerciseEquipmentMatchCounts.counts.get(opt) ?? 0,
              })),
              ...(ex.libExerciseEquipmentOptions.hasUnspecified
                ? [{
                    id: ex.LIB_EX_UNSPECIFIED,
                    label: 'Unspecified',
                    matchCount: ex.libExerciseEquipmentMatchCounts.unspecified,
                  } as LibFilterOption]
                : []),
            ]}
            selected={ex.libExerciseEquipmentFilter}
            onChange={(next) => ex.setLibExerciseEquipmentFilter(next as Set<string>)}
            onClose={() => ex.setLibExerciseOpenChip(null)}
            emptyMessage="No equipment values in your library yet."
            ariaLabel="Equipment filter"
          />
        )}
      </div>

      <div className="relative" ref={ex.libExerciseCreatedByChipRef}>
        <button
          type="button"
          className={`lib-chip ${(ex.libExerciseOpenChip === 'createdBy' || ex.libExerciseCreatedByFilter.size > 0) ? 'is-active' : ''}`}
          onClick={() => ex.setLibExerciseOpenChip(ex.libExerciseOpenChip === 'createdBy' ? null : 'createdBy')}
          aria-haspopup="dialog"
          aria-expanded={ex.libExerciseOpenChip === 'createdBy'}
        >
          <Filter />
          Created by
          {ex.libExerciseCreatedByFilter.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{ex.libExerciseCreatedByFilter.size}</span>
          )}
        </button>
        {ex.libExerciseOpenChip === 'createdBy' && (
          <LibraryFilterPopover
            options={[
              { id: 'You', label: 'You', matchCount: ex.libExerciseCreatedByMatchCounts.You },
              { id: 'Bravos', label: 'Bravos', matchCount: ex.libExerciseCreatedByMatchCounts.Bravos },
            ]}
            selected={ex.libExerciseCreatedByFilter as Set<string>}
            onChange={(next) => ex.setLibExerciseCreatedByFilter(next as Set<'You' | 'Bravos'>)}
            onClose={() => ex.setLibExerciseOpenChip(null)}
            ariaLabel="Created-by filter"
          />
        )}
      </div>

      <span className="lib-chip-divider" aria-hidden="true" />

      <div className="relative" ref={ex.libExerciseSortChipRef}>
        <button
          type="button"
          className={`lib-chip ${(ex.libExerciseOpenChip === 'sort' || ex.libExerciseSortShowsSelection) ? 'is-active' : ''}`}
          onClick={() => ex.setLibExerciseOpenChip(ex.libExerciseOpenChip === 'sort' ? null : 'sort')}
          aria-haspopup="listbox"
          aria-expanded={ex.libExerciseOpenChip === 'sort'}
        >
          <ArrowUpDown />
          <span className="lib-sort-label">
            {ex.libExerciseSortShowsSelection ? `Sort · ${ex.libExerciseCurrentSortLabel}` : 'Sort'}
          </span>
        </button>
        {ex.libExerciseOpenChip === 'sort' && (
          <div className="lib-popover lib-sort-popover" role="listbox" onKeyDown={(e) => { if (e.key === 'Escape') ex.setLibExerciseOpenChip(null); }}>
            {LIB_EX_SORT_OPTIONS.map((opt) => (
              <label key={opt.id} className={`lib-popover-row ${ex.libExerciseSort === opt.id ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name="lib-ex-sort"
                  checked={ex.libExerciseSort === opt.id}
                  onChange={() => {
                    ex.setLibExerciseSort(opt.id);
                    ex.setLibExerciseSortExplicit(true);
                    ex.setLibExerciseOpenChip(null);
                  }}
                />
                <span className="lib-popover-row-label">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {ex.libExercisesHasActiveControls && (
        <button
          type="button"
          className="lib-clear-all-chip"
          onClick={ex.handleClearAllExercises}
          aria-label="Clear all filters, search, and sort"
        >
          <X />
          Clear all
          <span className="lib-clear-all-chip-count lib-tnum">{ex.libExercisesAppliedCount}</span>
        </button>
      )}

      <div className="lib-results-meta">
        <strong className="lib-tnum">{ex.filteredAndSortedExercises.length}</strong>{' '}
        {ex.filteredAndSortedExercises.length === 1 ? 'result' : 'results'}
      </div>
    </div>
  );
}
